import { AppButton, AppInput, AppTextarea } from "components/form";
import { AppBooleanChip, AppChip, AppCopyButton, AppDialog, AppSectionErrorBoundary, AppSpacer } from "components/ui";
import { observer } from "mobx-react-lite";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { ITrackingDiagnostic } from "types/models/TrackingDiagnosticsModel";
import TrackingDiagnosticsAppDialogHeader from "../TrackingDiagnosticsAppDialogHeader";
import { getTrackingDiagnosticsSessionTypeColor, getTrackingDiagnosticsTypeColor } from "utilities/SystemUtils";

interface ITrackingDiagnosticsControlDialogProps {
    isOpened: boolean;
    onClose: () => void;
    trackingDiagnostic: ITrackingDiagnostic | null;
}

export const TrackingDiagnosticsManageDialog = observer(({ isOpened, onClose, trackingDiagnostic }: ITrackingDiagnosticsControlDialogProps) => {
    const location = useLocation();
    const { t } = useTranslation();

    const onCancelHandler = useCallback(() => {
        onClose();
    }, [onClose]);

    return (
        <AppDialog
            title={`${trackingDiagnostic?.id || ''}`}
            isOpened={isOpened}
            onClose={onClose}
            maxWidth={'lg'}
            customHeader={<TrackingDiagnosticsAppDialogHeader title={`${trackingDiagnostic?.id || ''}`} stickyHeader={true} trackingDiagnostic={trackingDiagnostic} />}
        >
            <div className='flex-auto d-flex flex-column justify-content-between'>
                <AppSectionErrorBoundary>
                    <div>
                        <section className='container'>
                            <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                <span className='mr-2'>{t('TrackingDiagnostics.Section.TrackingDiagnostic')}</span>

                            </h3>
                            <div className='d-flex flex-row my-3'>
                                <AppInput
                                    label={t('TrackingDiagnostics.Column.Id')}
                                    name={'id'}
                                    value={trackingDiagnostic?.id ?? '-'}
                                    disabled={true}
                                />
                                <AppSpacer isHorizontal space='1rem' />
                                <AppInput
                                    label={t('TrackingDiagnostics.Column.AddedAt')}
                                    name={'addedAt'}
                                    value={trackingDiagnostic?.addedAtUtc ?? '-'}
                                    disabled={true}
                                />
                            </div>
                            <AppSpacer space='5px' />
                            <div className='d-flex flex-row my-3'>
                                <AppInput
                                    label={t('TrackingDiagnostics.Column.BundleId')}
                                    name={'bundleId'}
                                    value={trackingDiagnostic?.bundleId ?? '-'}
                                    disabled={true}
                                />
                                <AppSpacer isHorizontal space='1rem' />
                                <AppInput
                                    label={t('TrackingDiagnostics.Column.SessionId')}
                                    name={'sessionId'}
                                    value={trackingDiagnostic?.sessionId ?? '-'}
                                    disabled={true}
                                />
                            </div>
                            <AppSpacer space='15px' />

                            <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                <span className='mr-2'>{t('TrackingDiagnostics.Section.Organization')}</span>
                            </h3>
                            <div className='d-flex flex-row my-3'>
                                <AppInput
                                    label={t('TrackingDiagnostics.Column.OrganizationId')}
                                    name={'organizationId'}
                                    value={trackingDiagnostic?.organizationId ?? '-'}
                                    disabled={true}
                                />
                                <AppSpacer isHorizontal space='1rem' />
                                <AppInput
                                    label={t('organizationName')}
                                    name={'organizationName'}
                                    value={trackingDiagnostic?.organizationName ?? '-'}
                                    disabled={true}
                                />
                            </div>
                            <AppSpacer space='15px' />

                            <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                <span className='mr-2'>{t('TrackingDiagnostics.Section.Requester')}</span>
                            </h3>
                            <div className='d-flex flex-row my-3'>
                                <AppInput
                                    label={t('TrackingDiagnostics.Column.RequesterId')}
                                    name={'requesterId'}
                                    value={trackingDiagnostic?.requesterId ?? '-'}
                                    disabled={true}
                                />
                                <AppSpacer isHorizontal space='1rem' />
                                <AppInput
                                    label={t('TrackingDiagnostics.Column.RequesterEmail')}
                                    name={'email'}
                                    value={trackingDiagnostic?.requesterEmail ?? '-'}
                                    disabled={true}
                                />
                            </div>
                            <AppSpacer space='15px' />

                            <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                <span className='mr-2'>{t('TrackingDiagnostics.Section.Info')}</span>
                            </h3>
                            <div className='d-flex flex-row my-3'>
                                <AppInput
                                    label={t('TrackingDiagnostics.Column.MeasurementId')}
                                    name={'measurementId'}
                                    value={trackingDiagnostic?.measurementId ?? '-'}
                                    disabled={true}
                                />
                                <AppSpacer isHorizontal space='1rem' />
                                <AppInput
                                    label={t('TrackingDiagnostics.Column.RadarSerial')}
                                    name={'radarSerial'}
                                    value={trackingDiagnostic?.radarSerial ?? '-'}
                                    disabled={true}
                                />
                            </div>

                            <div className='d-flex flex-row my-3 align-items-stretch'>
                                <div className='d-flex flex-column flex-fill h-100'>
                                    <div className='d-flex align-items-center gap-2'>
                                        <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                            <span className='mr-2'>{t('TrackingDiagnostics.Column.HasFile')}</span>
                                        </h3>
                                        <AppBooleanChip value={trackingDiagnostic?.hasFile ?? null} />
                                    </div>
                                </div>

                                <AppSpacer isHorizontal space='1rem' />

                                <div className='d-flex flex-column flex-fill h-100'>
                                    <div className='d-flex flex-column justify-content-center h-100'>
                                        <AppInput
                                            label={t('TrackingDiagnostics.Column.UploadedAt')}
                                            name={'uploadedAt'}
                                            value={`${trackingDiagnostic?.uploadedAtUtc ?? '-'}`}
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                            </div>
                            <AppSpacer space='15px' />

                            <AppTextarea
                                label={t('TrackingDiagnostics.Column.Description')}
                                name={'description'}
                                value={trackingDiagnostic?.description ?? '-'}
                                disabled={true}
                                onChange={() => { }}
                            />
                            <AppSpacer space='15px' />
                            <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                <span className='mr-2'>{t('TrackingDiagnostics.Section.Tag')}</span>
                            </h3>
                            <div className='d-flex flex-row gap-10px my-3'>
                                {
                                    <AppChip<string> value={trackingDiagnostic?.type ?? null} getColor={() => getTrackingDiagnosticsTypeColor(trackingDiagnostic?.type ?? null)} />
                                }
                            </div>
                            <AppSpacer space='15px' />

                            <h3 className='font-15 font-700 font-lato text-capitalize mr-2 line-height-23'>
                                <span className='mr-2'>{t('TrackingDiagnostics.Section.Session')}</span>
                            </h3>
                            <div className='d-flex flex-row gap-10px my-3'>
                                <AppChip<string> value={trackingDiagnostic?.sessionType ?? null} getColor={() => getTrackingDiagnosticsSessionTypeColor(trackingDiagnostic?.sessionType ?? null)} />
                            </div>
                        </section>
                        <AppSpacer space='20px' />
                    </div>
                    <footer className='d-flex align-items-center justify-content-between p-3 bg-white position-sticky border-top bottom-0 left-0 w-100 z-index-9'>
                        <AppButton variant='secondary' onClick={onCancelHandler} data-test='user-dialog-cancel-button'>
                            {t('cancel')}
                        </AppButton>
                        <AppCopyButton localization={t('UserDiagnostics.Action.CopyUrl')} text={`${window.location.origin}${location.pathname}${location.search}`} />
                    </footer>
                </AppSectionErrorBoundary>
            </div>
        </AppDialog>
    );
});